import type { FC, ReactNode } from 'react'
import type { QuoteLine } from '@/types'

import Link from 'next/link'
import { CircleAlert, Tag, Trash2, TrendingDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'
import { unitLabel } from '@/constants/units'
import { Badge } from '@/components/ui/badge'
import { CmsText } from '@/components/store/cms-text'
import { Num, Price } from '@/components/store/price'
import { ProductMedia } from '@/components/store/product-media'
import { QuantityStepper } from '@/components/store/quantity-stepper'

interface CartLineItemProps {
    line: QuoteLine
    onQty: (key: string, qty: number) => void
    onRemove: (key: string) => void
    compact?: boolean
    onNavigate?: () => void
}

export const CartLineItem: FC<CartLineItemProps> = ({ line, onQty, onRemove, compact, onNavigate }): ReactNode => {
    const { tx, txn, locale } = useI18n()
    const { pricing } = line
    const unit = unitLabel(line.unit, locale, line.qty)

    const issueMessage = (): ReactNode => {
        if (line.issue === 'out-of-stock') return tx('نفد من المخزون، احذفه لإكمال الطلب.', 'Out of stock. Remove it to continue.')
        if (line.issue === 'insufficient-stock' && line.maxQty !== null) return txn('المتاح {n} فقط.', 'Only {n} available.', { n: <Num value={line.maxQty} /> })
        if (line.issue === 'below-moq') return txn('أقل كمية للطلب {n}.', 'The minimum order is {n}.', { n: <Num value={line.minOrderQty} /> })
        if (line.issue === 'too-many-lines') return tx('السلة تتسع لـ 20 منتجاً مختلفاً كحد أقصى.', 'A cart can hold up to 20 different products.')

        return null
    }

    const fix = (): void => {
        if (line.issue === 'insufficient-stock' && line.maxQty !== null) onQty(line.key, line.maxQty)
        if (line.issue === 'below-moq') onQty(line.key, line.minOrderQty)
    }

    return (
        <div className={cn('flex gap-4', compact ? 'py-4' : 'py-5')}>
            <Link href={`/products/${line.slug}`} onClick={onNavigate} className={cn('shrink-0 overflow-hidden rounded-2xl ring-1 ring-navy-100', compact ? 'size-20' : 'size-24 sm:size-28')}>
                <ProductMedia src={line.image} alt={line.imageAlt} className='h-full w-full' />
            </Link>

            <div className='flex min-w-0 flex-1 flex-col gap-2'>
                <div className='flex items-start justify-between gap-3'>
                    <div className='flex min-w-0 flex-col gap-1'>
                        <Link href={`/products/${line.slug}`} onClick={onNavigate} className='font-medium leading-snug text-navy-950 transition-colors hover:text-cyan-700'>
                            <CmsText table='products' id={line.productId} field={line.name} />
                        </Link>

                        {(line.variantSize || line.variantColor) && (
                            <p className='flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground'>
                                {line.colorHex && <span className='size-3 rounded-full ring-1 ring-navy-200' style={{ backgroundColor: line.colorHex }} aria-hidden='true' />}
                                {line.variantSize && <CmsText table='product_variants' id={line.variantId} field={line.variantSize} />}
                                {line.variantSize && line.variantColor && <span aria-hidden='true'>·</span>}
                                {line.variantColor && <CmsText table='product_variants' id={line.variantId} field={line.variantColor} />}
                            </p>
                        )}
                    </div>

                    <button
                        type='button'
                        onClick={() => onRemove(line.key)}
                        className='-me-1 -mt-1 inline-flex size-8 shrink-0 items-center justify-center rounded-full text-navy-400 transition-colors hover:bg-red-50 hover:text-red-600'
                        aria-label={tx('حذف من السلة', 'Remove from cart')}
                    >
                        <Trash2 className='size-4' />
                    </button>
                </div>

                <div className='flex flex-wrap items-center gap-1.5'>
                    {pricing.tier === 'wholesale'
                        ? <Badge tone='cyan'>{tx('سعر الجملة', 'Wholesale price')}</Badge>
                        : <Badge tone='gray'>{tx('سعر القطاعي', 'Retail price')}</Badge>}

                    {line.offerTitle && pricing.offer && (
                        <Badge tone='amber'>
                            <Tag className='size-3' aria-hidden='true' />
                            <CmsText table='offers' id={pricing.offer.id} field={line.offerTitle} className='max-w-[180px] truncate' />
                        </Badge>
                    )}
                </div>

                <div className='flex flex-wrap items-end justify-between gap-3'>
                    <div className='flex flex-col gap-1.5'>
                        <QuantityStepper
                            value={line.qty}
                            min={1}
                            max={line.maxQty}
                            size='sm'
                            label={tx('الكمية', 'Quantity')}
                            onChange={qty => onQty(line.key, qty)}
                        />
                        <span className='text-xs text-muted-foreground'>
                            <Price value={pricing.finalUnit} />
                            {' / '}
                            {unit}
                        </span>
                    </div>

                    <div className='flex flex-col items-end gap-0.5'>
                        {pricing.discount > 0 && <Price value={pricing.subtotal} strike className='text-xs text-navy-400' />}
                        <Price value={pricing.total} className='text-base font-semibold text-navy-950' />
                    </div>
                </div>

                {pricing.qtyToWholesale > 0 && pricing.wholesaleUnit !== null && !line.issue && (
                    <button
                        type='button'
                        onClick={() => onQty(line.key, line.wholesaleMinQty)}
                        className='flex items-center gap-2 rounded-xl bg-cyan-50 px-3 py-2 text-start text-xs text-cyan-900 ring-1 ring-cyan-100 transition-colors hover:bg-cyan-100'
                    >
                        <TrendingDown className='size-4 shrink-0 text-cyan-700' aria-hidden='true' />
                        <span className='leading-relaxed'>
                            {txn('أضف {n} للحصول على سعر الجملة {price} للوحدة', 'Add {n} more to unlock the wholesale price of {price} each', {
                                n: <strong className='font-semibold'><Num value={pricing.qtyToWholesale} /></strong>,
                                price: <strong className='font-semibold'><Price value={pricing.wholesaleUnit} /></strong>
                            })}
                        </span>
                    </button>
                )}

                {pricing.wholesaleSavings > 0 && (
                    <p className='text-xs font-medium text-emerald-700'>
                        {txn('وفّرت {amount} بسعر الجملة', 'You save {amount} with wholesale pricing', { amount: <Price value={pricing.wholesaleSavings} /> })}
                    </p>
                )}

                {line.issue && (
                    <div className='flex flex-wrap items-center gap-2 rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700 ring-1 ring-red-100' role='alert'>
                        <CircleAlert className='size-4 shrink-0' aria-hidden='true' />
                        <span className='flex-1'>{issueMessage()}</span>
                        {(line.issue === 'insufficient-stock' || line.issue === 'below-moq') && (
                            <button type='button' onClick={fix} className='rounded-full bg-white px-2.5 py-1 font-semibold text-red-700 ring-1 ring-red-200 transition-colors hover:bg-red-100'>
                                {tx('تعديل الكمية', 'Fix quantity')}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
